import { Inject, Injectable } from "@angular/core";
import { RxState } from "@rx-angular/state";
import { catchError, delay, map, of, shareReplay, BehaviorSubject, combineLatest, startWith } from "rxjs";

import { LoggerService } from "../../logger/logger.service";
import { LabelsState } from "../models/assessments-labels.state.interface";
import { AssessmentCustomLabel, AssessmentResourceId } from "src/app/api/models/assessments/assessment.interface";
import { API_CLIENT_LABELS_SERVICE } from "src/app/api/injectors/api-client-labels.token";
import { BaseApiClientLabelsService } from "src/app/api/base-api-client-labels.service";

@Injectable({
    providedIn: 'root'
})
export class AssessmentsLabelsStore extends RxState<LabelsState> {

    private readonly loggerName: string = 'LabelsStore';

    private readonly initState = {
        empty: undefined,
        loading: true,
        labels: new Map<AssessmentResourceId, AssessmentCustomLabel>(),
    } as LabelsState;

    // Subject to track local label updates
    private readonly _localUpdates$ = new BehaviorSubject<Map<AssessmentResourceId, AssessmentCustomLabel>>(new Map());

    // Initial API call (only called once)
    private readonly _initialLabels$ = this.apiClientLabelsService.getLabels().pipe(
        delay(100),
        map(labels => new Map(labels.map(label => ([label.id, label])))),
        catchError(err => {
            console.error(err);
            this.logger.error(this.loggerName, err);
            return of(new Map());
        }),
        // loadLabels() subscribes twice (combineLatest + the local-updates seed); one request, not two.
        shareReplay(1)
    );

    // Combined stream that merges initial API data with local updates
    private readonly _labels$ = combineLatest([
        this._initialLabels$,
        this._localUpdates$.pipe(startWith(new Map()))
    ]).pipe(
        map(([initialLabels, localUpdates]) => {
            const combined = new Map(initialLabels);
            localUpdates.forEach((label, id) => {
                combined.set(id, label);
            });
            return combined;
        }),
        shareReplay(1)
    );

    constructor(
        private logger: LoggerService,
        @Inject(API_CLIENT_LABELS_SERVICE) private apiClientLabelsService: BaseApiClientLabelsService
    ) {
        super();
        this.set(this.initState);
    }

    loadLabels(): void {
        this.connect('labels', this._labels$);
        this.connect('loading', this._labels$.pipe(map(() => false)));
        this.connect('empty', this._labels$.pipe(map(labels => !labels?.size)));
        
        // Initialize local updates with initial data
        this._initialLabels$.subscribe(initialLabels => {
            this._localUpdates$.next(initialLabels);
        });
    }

    addLabel(newLabel: AssessmentCustomLabel): void {
        this.set((state) => ({
            labels: new Map(state.labels).set(newLabel.id, newLabel),
        }));
        
        // Also emit to local updates to trigger reactive updates
        const currentUpdates = this._localUpdates$.value;
        const updatedMap = new Map(currentUpdates);
        updatedMap.set(newLabel.id, newLabel);
        this._localUpdates$.next(updatedMap);
    }

    updateLabel(updatedLabel: AssessmentCustomLabel): void {
        const labels = this.get('labels');
        labels.set(updatedLabel.id, updatedLabel);
        this.set('labels', () => new Map(labels.entries()));
        
        // Also emit to local updates to trigger reactive updates
        const currentUpdates = this._localUpdates$.value;
        const updatedMap = new Map(currentUpdates);
        updatedMap.set(updatedLabel.id, updatedLabel);
        this._localUpdates$.next(updatedMap);
    }
    
    deleteLabel(id: number): void {
        const updatedLabels = new Map(this.get('labels'));
        updatedLabels.delete(id);
        this.set('labels', () => updatedLabels);
        
        // Also emit to local updates to trigger reactive updates
        const currentUpdates = this._localUpdates$.value;
        const updatedMap = new Map(currentUpdates);
        updatedMap.delete(id);
        this._localUpdates$.next(updatedMap);
    }
}
